"use client"

import React, { useState, useEffect } from "react"
import axios from "axios"
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from "@mui/material"
import {
  Person as PersonIcon,
  Campaign as CampaignIcon,
  Message as MessageIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material"
import { useSelector } from "react-redux"
import { API_URL } from "../../constants"

const ResellerDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { userInfo } = useSelector((state) => state.auth)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }

        const { data } = await axios.get(`${API_URL}/api/dashboard/reseller`, config)
        setDashboardData(data)
        setLoading(false)
      } catch (error) {
        setError(error.response && error.response.data.message ? error.response.data.message : error.message)
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [userInfo])

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    )
  }

  if (!dashboardData) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No dashboard data available.
      </Alert>
    )
  }

  // Provide default values for all properties to avoid undefined errors
  const stats = dashboardData.stats || {}
  const campaignPerformance = dashboardData.campaignPerformance || {
    completed: 0,
    pending: 0,
    processing: 0,
    failed: 0,
  }
  const recentTransactions = dashboardData.recentTransactions || []
  const topClients = dashboardData.topClients || []

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
            <Box
              sx={{
                height: 4,
                bgcolor: "info.main",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            />
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <PersonIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6" component="h3">
                  Clients
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: "bold", my: 1 }}>
                {stats.totalClients || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.activeClients || 0} active clients
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
            <Box
              sx={{
                height: 4,
                bgcolor: "secondary.main",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            />
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <CampaignIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6" component="h3">
                  Campaigns
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: "bold", my: 1 }}>
                {stats.totalCampaigns || 0}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip
                  size="small"
                  icon={<CheckCircleIcon fontSize="small" />}
                  label={`${stats.completedCampaigns || 0} completed`}
                  color="success"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
            <Box
              sx={{
                height: 4,
                bgcolor: "success.main",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            />
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <MessageIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" component="h3">
                  Messages
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: "bold", my: 1 }}>
                {(stats.totalMessages || 0).toLocaleString()}
              </Typography>
              
            </CardContent>
          </Card>
        </Grid> */}

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
            <Box
              sx={{
                height: 4,
                bgcolor: "warning.main",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            />
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <CreditCardIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6" component="h3">
                  Credits
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: "bold", my: 1 }}>
                {stats.availableCredits || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.usedCredits || 0} credits used
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography variant="h6" component="h2">
                Recent Credit Transactions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Latest credit activities
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {recentTransactions && recentTransactions.length > 0 ? (
                recentTransactions.map((transaction) => {
                  const sanitize = (text) =>
                    typeof text === "string"
                      ? text
                        .replace(/\s*\$?\s*Report\s+manipulated:\s*(true|false)\s*\$?\s*/gi, "")
                        .trim()
                      : text;


                  const primaryText =
                    transaction.title ||
                    `${transaction.transactionType === "credit" ? "Credit Added" : "Charged"}: ${transaction.amount || 0} ${transaction.creditType || ""
                    } credits`

                  const secondaryText =
                    sanitize(transaction.description) ||
                    [
                      transaction.assignedBy?.name
                        ? `By ${transaction.assignedBy.name}${transaction.assignedBy.role ? ` (${transaction.assignedBy.role})` : ""
                        }`
                        : null,
                      transaction.creditType ? `Type: ${transaction.creditType}` : null,
                    ]
                      .filter(Boolean)
                      .join(" • ")

                  return (
                    <React.Fragment key={transaction._id}>
                      {transaction._id > 0 && <Divider component="li" />}
                      <ListItem sx={{ py: 2, px: 3, "&:hover": { bgcolor: "action.hover" } }}>
                        <ListItemAvatar>
                          <Avatar
                            sx={{ bgcolor: transaction.transactionType === "credit" ? "success.main" : "info.main" }}
                            aria-label={transaction.transactionType === "credit" ? "credit added" : "credit debited"}
                          >
                            <ReceiptIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={primaryText} secondary={secondaryText} />
                        <Typography variant="body2" color="text.secondary">
                          {transaction.createdAt ? new Date(transaction.createdAt).toLocaleDateString() : "N/A"}
                        </Typography>
                      </ListItem>
                    </React.Fragment>
                  )
                })
              ) : (
                <ListItem>
                  <ListItemText primary="No recent transactions" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography variant="h6" component="h2">
                Campaign Status Overview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status of all campaigns
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2">Completed</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.totalCampaigns > 0
                      ? ((stats.completedCampaigns / stats.totalCampaigns) * 100).toFixed(1)
                      : 0}
                    %
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.totalCampaigns > 0 ? (stats.completedCampaigns / stats.totalCampaigns) * 100 : 0}
                  color="success"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2">Pending</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.totalCampaigns > 0 ? ((stats.pendingCampaigns / stats.totalCampaigns) * 100).toFixed(1) : 0}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.totalCampaigns > 0 ? (stats.pendingCampaigns / stats.totalCampaigns) * 100 : 0}
                  color="warning"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2">Processing</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.totalCampaigns > 0
                      ? ((stats.processingCampaigns / stats.totalCampaigns) * 100).toFixed(1)
                      : 0}
                    %
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.totalCampaigns > 0 ? (stats.processingCampaigns / stats.totalCampaigns) * 100 : 0}
                  color="info"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2">Failed</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.totalCampaigns > 0 ? ((stats.rejectedCampaigns / stats.totalCampaigns) * 100).toFixed(1) : 0}
                    %
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.totalCampaigns > 0 ? (stats.rejectedCampaigns / stats.totalCampaigns) * 100 : 0}
                  color="error"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              {/* <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Button variant="contained" color="primary" href="/campaigns">
                  View All Campaigns
                </Button>
              </Box> */}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={1} sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography variant="h6" component="h2">
                Top Clients
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Clients with most campaign activity
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client Name</TableCell>
                    <TableCell>Campaigns</TableCell>
                    <TableCell>Messages Sent</TableCell>
                    {/* <TableCell>Delivery Rate</TableCell> */}
                    <TableCell>Credits Used</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topClients && topClients.length > 0 ? (
                    topClients.map((client, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{client.name || "N/A"}</TableCell>
                        <TableCell>{client.campaigns || 0}</TableCell>
                        <TableCell>{(client.messagesSent || 0).toLocaleString()}</TableCell>
                        {/* <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            {client.deliveryRate || 0}%
                            {Number.parseFloat(client.deliveryRate || 0) > 95 && (
                              <TrendingUpIcon fontSize="small" color="success" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell> */}
                        <TableCell>{client.creditsUsed || 0}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No client data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default ResellerDashboard
